import type { DashboardLinkedResource, LinkedResource } from "./linked-resources";
import type { TagSummary } from "./tags";

/**
 * Canonical task statuses. The single source of truth — UI selectors,
 * filters, and the API Zod schema (`taskStatusSchema`) all derive from this
 * tuple. Add a new status here and it propagates everywhere.
 */
export const TASK_STATUSES = [
  "triage",
  "backlog",
  "todo",
  "in_progress",
  "in_review",
  "blocked",
  "done",
  "canceled",
] as const;

export type TaskStatus = (typeof TASK_STATUSES)[number];

/**
 * User-facing labels for each status. Keep these in sync with `TASK_STATUSES`
 * (the `Record<TaskStatus, ...>` type guarantees exhaustiveness).
 */
export const TASK_STATUS_LABELS: Record<TaskStatus, string> = {
  triage: "Triage",
  backlog: "Backlog",
  todo: "To do",
  in_progress: "In progress",
  in_review: "In review",
  blocked: "Blocked",
  done: "Done",
  canceled: "Canceled",
};

/**
 * Canonical display order for status lists and filter dropdowns. Triage
 * and Backlog lead because freshly-created tasks land there.
 */
export const TASK_STATUS_DISPLAY_ORDER: readonly TaskStatus[] = [
  "triage",
  "backlog",
  "todo",
  "in_progress",
  "in_review",
  "blocked",
  "done",
  "canceled",
] as const;

/** Statuses users can pick directly; canceled is handled by cancellation flows. */
export const TASK_STATUS_SELECTABLE_ORDER: readonly Exclude<TaskStatus, "canceled">[] = [
  "triage",
  "backlog",
  "todo",
  "in_progress",
  "in_review",
  "blocked",
  "done",
] as const;

/**
 * Canonical task priorities. Single source of truth — UI selectors, badges,
 * and payload validation derive from this tuple. Ordered low → urgent.
 */
export const TASK_PRIORITIES = ["low", "medium", "high", "urgent"] as const;

export type TaskPriority = (typeof TASK_PRIORITIES)[number];

/** The DB default priority applied when a task is created without one. */
export const DEFAULT_TASK_PRIORITY: TaskPriority = "medium";

/** User-facing labels for each priority. */
export const TASK_PRIORITY_LABELS: Record<TaskPriority, string> = {
  low: "Low",
  medium: "Medium",
  high: "High",
  urgent: "Urgent",
};

export type ReviewQuestionKind =
  | "submission_match"
  | "account_match"
  | "product_match"
  | "free_form";

export type ReviewQuestionOption = {
  id: string;
  label: string;
  description?: string;
  payload?: Record<string, unknown>;
};

export type ReviewQuestion = {
  id: string;
  kind: ReviewQuestionKind;
  prompt: string;
  helperText?: string;
  multiSelect: boolean;
  options: ReviewQuestionOption[];
  allowCustomAnswer: boolean;
  /**
   * Option ids the wizard should render as already-selected when the user
   * lands on this question. Used by confirm-style flows (e.g. triage
   * always-review mode) where the human is verifying or changing a pick
   * the agent already made. The wizard treats a question with preselects
   * as "explicit Continue" rather than auto-advance, so the human must
   * actively affirm rather than implicitly accept.
   */
  defaultSelectedOptionIds?: string[];
};

export type ReviewAnswer = {
  question_id: string;
  selected_option_ids: string[];
  custom_answer?: string;
};

export type Task = {
  id: string;
  task_number: number | null;
  title: string;
  description: string | null;
  status: TaskStatus;
  priority: TaskPriority;
  assignee_type: "user" | "agent" | null;
  assignee_user_id: string | null;
  assignee_user_email: string | null;
  assignee_agent_id: string | null;
  assignee_agent_name: string | null;
  due_date: string | null;
  /**
   * When set on an agent-assigned task, the assignment run is deferred until
   * this time — the agent picks the task up then instead of immediately. The
   * fire consumes it (the column is cleared when the scheduled run starts).
   */
  scheduled_at: string | null;
  labels: string[];
  acceptance_criteria: string[];
  /** Present when the task is a workflow-gated review. Empty array otherwise. */
  questions: ReviewQuestion[];
  /** Filled when the human resolves a review task. Null otherwise. */
  answers: ReviewAnswer[] | null;
  resolved_at: string | null;
  resolved_by_user_id: string | null;
  outcome_summary: string | null;
  workflow_run_id: string | null;
  workflow_step_node_id: string | null;
  primary_resource_type: string | null;
  primary_resource_id: string | null;
  primary_resource_name: string | null;
  completed_at: string | null;
  created_by_user_id: string | null;
  created_by_user_email: string | null;
  created_at: string;
  updated_at: string;
  parent_task_id: string | null;
  /**
   * An agent has a live run on this task right now — i.e. an `agent_threads`
   * row with `source = "task"` in one of {@link ACTIVE_EXECUTION_STATUSES}.
   * Distinct from `assignee_agent_id`, which only says who would run it.
   */
  agent_working: boolean;
};

export type DashboardTask = Task & {
  primary_resource_model_name: string | null;
};

/** Minimal parent-task summary surfaced on `TaskDetail.parent_task`. */
export type TaskParentSummary = {
  id: string;
  task_number: number | null;
  title: string;
  status: TaskStatus;
};

export type TaskParticipant = {
  id: string;
  participant_type: "user" | "agent";
  participant_user_id: string | null;
  participant_agent_id: string | null;
  role: string;
  created_by_user_id: string | null;
  created_at: string;
};

export type TaskWatcher = {
  id: string;
  watchable_type: string;
  watchable_id: string;
  user_id: string | null;
  user_email: string | null;
  created_at: string;
};

export type TaskComment = {
  id: string;
  author_type: "user" | "agent";
  author_user_id: string | null;
  author_user_email: string | null;
  author_agent_id: string | null;
  execution_id: string | null;
  content: string;
  created_at: string;
  updated_at: string | null;
};

/**
 * A message in a task run. A task run is a thread (agent_threads, source="task")
 * and its step log is the thread's agent_messages — assistant messages carry the
 * rich `parts` (tool calls/results, reasoning, text) the conversation UI renders.
 */
export type AgentRunMessage = {
  id: string;
  role: string;
  content: string | null;
  parts: unknown[] | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
};

export type TaskExecution = {
  id: string;
  agent_id: string | null;
  status: string;
  last_error: string | null;
  last_error_code: string | null;
  superseded_by_execution_id: string | null;
  /** The thread (ath_…) that delegated this run; null when a person or schedule started it. */
  parent_thread_id: string | null;
  /** Top of the delegation tree this run belongs to; null when nothing delegated it. */
  root_thread_id: string | null;
  started_at: string | null;
  completed_at: string | null;
  failed_at: string | null;
  canceled_at: string | null;
  created_at: string;
  updated_at: string;
  messages: AgentRunMessage[];
};

export type TaskEvent = {
  id: string;
  event_type: string;
  actor_type: string | null;
  actor_user_id: string | null;
  actor_user_email: string | null;
  actor_agent_id: string | null;
  payload_json: Record<string, unknown>;
  created_at: string;
};

export type TaskLinkedResource = LinkedResource;

export type TaskDetail = Task & {
  tags: TagSummary[];
  participants: TaskParticipant[];
  comments: TaskComment[];
  events: TaskEvent[];
  linked_resources: TaskLinkedResource[];
  watchers: TaskWatcher[];
  isWatching: boolean;
  parent_task: TaskParentSummary | null;
  subtask_counts: { total: number; completed: number };
};

export type DashboardTaskDetail = Omit<TaskDetail, keyof Task | "linked_resources"> &
  DashboardTask & {
    linked_resources: DashboardLinkedResource[];
  };

export type CreateTaskPayload = {
  title: string;
  description?: string;
  status?: TaskStatus;
  priority?: TaskPriority;
  assignee_type?: "user" | "agent" | null;
  assignee_user_id?: string | null;
  assignee_agent_id?: string | null;
  due_date?: string | null;
  /** Defers the agent assignment's run until this time. Requires an agent assignee; must not be in the past. */
  scheduled_at?: string | null;
  labels?: string[];
  acceptance_criteria?: string[];
  primary_resource_type?: string | null;
  primary_resource_id?: string | null;
  parent_task_id?: string | null;
};

export type ActiveExecutionDisposition = "cancel" | "keep";

export type UpdateTaskPayload = Omit<Partial<CreateTaskPayload>, "description"> & {
  description?: string | null;
  outcome_summary?: string | null;
  /**
   * Required when PATCHing the assignee while a task-source agent thread is
   * still active. `cancel` stops that run; `keep` leaves it running after the
   * assignee change. Omitting it with an active run returns 409.
   */
  active_execution_disposition?: ActiveExecutionDisposition;
};

export type CreateCommentPayload = {
  content: string;
  mentioned_agent_ids?: string[];
};
